import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, Extension, Node } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Image } from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import mermaid from "mermaid";
import {
  Bold, Italic, Underline as UIcon, Strikethrough, List, ListOrdered,
  Heading1, Heading2, Heading3, Quote, Code, Link2, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo, Redo, Highlighter, Palette,
  Workflow, Network, GitBranch, ImageIcon, Eraser, Superscript as SupIcon, Subscript as SubIcon,
  IndentIncrease, IndentDecrease, Minus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

// -------- Custom TipTap extensions for MS Word-like typography --------
// Single extension owning font-family, font-size, line-height so they merge into ONE `style` on <span>.
const InlineTypography = Extension.create({
  name: "inlineTypography",
  addOptions() { return { types: ["textStyle"] }; },
  addGlobalAttributes() {
    const build = (val, key, cur) => {
      const styles = [];
      if (cur.fontFamily) styles.push(`font-family: ${cur.fontFamily}`);
      if (cur.fontSize) styles.push(`font-size: ${cur.fontSize}`);
      return styles.length ? { style: styles.join("; ") } : {};
    };
    return [{
      types: this.options.types,
      attributes: {
        fontFamily: {
          default: null,
          parseHTML: el => el.style.fontFamily?.replace(/['"]/g, "") || null,
          renderHTML: attrs => attrs.fontFamily ? { style: `font-family: ${attrs.fontFamily}` } : {},
        },
        fontSize: {
          default: null,
          parseHTML: el => el.style.fontSize || null,
          // Deliberate: include font-family in the same style string when both exist
          renderHTML: attrs => {
            const parts = [];
            if (attrs.fontSize) parts.push(`font-size: ${attrs.fontSize}`);
            if (attrs.fontFamily) parts.push(`font-family: ${attrs.fontFamily}`);
            return parts.length ? { style: parts.join("; ") } : {};
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: size => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
      setFontFamily: fam => ({ chain }) => chain().setMark("textStyle", { fontFamily: fam }).run(),
      unsetFontFamily: () => ({ chain }) => chain().setMark("textStyle", { fontFamily: null }).removeEmptyTextStyle().run(),
    };
  },
});

// Line-height on block nodes
const LineHeight = Extension.create({
  name: "lineHeight",
  addOptions() { return { types: ["paragraph","heading"], defaultLineHeight: null }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        lineHeight: {
          default: this.options.defaultLineHeight,
          parseHTML: el => el.style.lineHeight || null,
          renderHTML: attrs => attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      setLineHeight: v => ({ commands }) => this.options.types.every(t => commands.updateAttributes(t, { lineHeight: v })),
      unsetLineHeight: () => ({ commands }) => this.options.types.every(t => commands.updateAttributes(t, { lineHeight: null })),
    };
  },
});

// Real page-break as a block Node (HR-based, class 'page-break' → prints on new page)
const PageBreak = Node.create({
  name: "pageBreak",
  group: "block",
  atom: true,
  selectable: false,
  parseHTML() { return [{ tag: "hr.page-break" }, { tag: "div.page-break" }]; },
  renderHTML() {
    return ["hr", { class: "page-break", style: "page-break-after: always; border: 0; border-top: 1px dashed #ccc; margin: 12px 0;" }];
  },
  addCommands() { return { insertPageBreak: () => ({ commands }) => commands.insertContent({ type: this.name }) }; },
});

// Paragraph indent — works on paragraph & heading, ~24px steps
const ParagraphIndent = Extension.create({
  name: "paragraphIndent",
  addOptions() { return { types: ["paragraph","heading","blockquote"], step: 24, max: 8 }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        indent: {
          default: 0,
          parseHTML: el => parseInt(el.style.paddingLeft || 0, 10) / 24 || 0,
          renderHTML: attrs => attrs.indent ? { style: `padding-left: ${attrs.indent * 24}px` } : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      indentParagraph: () => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (this.options.types.includes(node.type.name)) {
            const cur = node.attrs.indent || 0;
            const next = Math.min(cur + 1, this.options.max);
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        });
        return changed;
      },
      outdentParagraph: () => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (this.options.types.includes(node.type.name)) {
            const cur = node.attrs.indent || 0;
            const next = Math.max(cur - 1, 0);
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        });
        return changed;
      },
    };
  },
});

mermaid.initialize({ startOnLoad: false, theme: "default", securityLevel: "loose", themeVariables: {
  primaryColor: "#F3E9E4", primaryTextColor: "#111827", primaryBorderColor: "#C05A41",
  lineColor: "#C05A41", secondaryColor: "#DBEAFE", tertiaryColor: "#DCFCE7", fontFamily: "IBM Plex Sans, sans-serif",
}});

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Georgia", value: "Georgia, 'Times New Roman', serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Cambria", value: "Cambria, Georgia, serif" },
  { label: "Calibri", value: "Calibri, Arial, sans-serif" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "IBM Plex Sans", value: "'IBM Plex Sans', system-ui, sans-serif" },
  { label: "Cormorant Garamond", value: "'Cormorant Garamond', Georgia, serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
];
const FONT_SIZES = ["8pt","9pt","10pt","10.5pt","11pt","12pt","14pt","16pt","18pt","20pt","24pt","28pt","36pt"];
const LINE_HEIGHTS = [
  { label:"Single", value:"1" }, { label:"1.15", value:"1.15" }, { label:"1.5", value:"1.5" },
  { label:"Double", value:"2" }, { label:"2.5", value:"2.5" },
];

const HL_COLORS = [
  { name: "Yellow", value: "#FEF3C7", cls: "hl-yellow" },
  { name: "Pink", value: "#FCE7F3", cls: "hl-pink" },
  { name: "Blue", value: "#DBEAFE", cls: "hl-blue" },
  { name: "Green", value: "#DCFCE7", cls: "hl-green" },
  { name: "Purple", value: "#EDE9FE", cls: "hl-purple" },
  { name: "Orange", value: "#FFEDD5", cls: "hl-orange" },
];
const TEXT_COLORS = ["#111827", "#C05A41", "#1D4ED8", "#059669", "#7C3AED", "#B45309", "#DC2626"];

const DIAGRAM_TEMPLATES = {
  Flowchart: `flowchart TD
  A[Research Question] --> B{Feasible?}
  B -- Yes --> C[Design Methodology]
  B -- No --> D[Refine Question]
  C --> E[Data Collection]
  E --> F[Analysis]
  F --> G[Publish & Disseminate]
  style A fill:#F3E9E4,stroke:#C05A41,color:#111827
  style G fill:#DCFCE7,stroke:#059669,color:#111827`,
  Workflow: `flowchart LR
  Y1["Year 1<br/>Setup"] --> Y2["Year 2<br/>Pilot Study"]
  Y2 --> Y3["Year 3<br/>Full Study"]
  Y3 --> Y4["Year 4<br/>Analysis"]
  Y4 --> Y5["Year 5<br/>Dissemination"]
  style Y1 fill:#DBEAFE,stroke:#1D4ED8
  style Y3 fill:#FEF3C7,stroke:#B45309
  style Y5 fill:#DCFCE7,stroke:#059669`,
  Mindmap: `mindmap
  root((Research))
    Objectives
      Discover new mechanism
      Publish findings
    Methodology
      Experiments
      Simulations
      Field studies
    Team
      PI
      Co-PIs
      Students
    Impact
      Academic
      Societal
      Policy`,
  Sequence: `sequenceDiagram
  participant PI
  participant Team
  participant Reviewers
  PI->>Team: Kickoff & assign roles
  Team-->>PI: Weekly progress
  PI->>Reviewers: Interim report (M6)
  Reviewers-->>PI: Feedback
  PI->>Reviewers: Final submission`,
  Gantt: `gantt
  title Project Timeline
  dateFormat  YYYY-MM
  section Setup
  Hiring          :a1, 2026-06, 3M
  Ethics approval :a2, 2026-07, 2M
  section Execution
  Data collection :b1, after a1, 8M
  Analysis        :b2, after b1, 4M
  section Output
  Manuscripts     :c1, after b2, 6M`,
};

async function renderMermaidSVG(code) {
  const id = "mmd-" + Math.random().toString(36).slice(2);
  try {
    const { svg } = await mermaid.render(id, code);
    return svg;
  } catch (e) {
    return `<pre style="color:#DC2626">Diagram error: ${e?.message || e}</pre>`;
  }
}

export default function RichEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Link and Underline are configured explicitly below OR bundled by StarterKit;
        // in tiptap v3 StarterKit includes them, so we don't add them separately to avoid duplicates.
      }),
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      InlineTypography,
      LineHeight,
      PageBreak,
      ParagraphIndent,
      Subscript,
      Superscript,
      Color,
      Image.configure({ allowBase64: true, HTMLAttributes: { class: "rounded-md border border-border max-w-full my-3" } }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "border-collapse w-full my-3" } }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: placeholder || "Start writing…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-editor focus:outline-none min-h-[520px] px-1 py-2 leading-relaxed",
      },
    },
  });

  // Update content when parent switches sections or applies AI-generated content.
  // Compare on trimmed lengths + first 200 chars so TipTap's HTML normalisation doesn't skip a real change.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML() || "";
    const next = value || "";
    if (current === next) return;
    // Cheap heuristic: if lengths differ notably OR the first 200 chars differ, force refresh.
    if (Math.abs(current.length - next.length) > 4 || current.slice(0, 200) !== next.slice(0, 200)) {
      editor.commands.setContent(next, false);
    }
    // eslint-disable-next-line
  }, [value, editor]);

  const [diagOpen, setDiagOpen] = useState(false);
  const [diagKind, setDiagKind] = useState("Flowchart");
  const [diagCode, setDiagCode] = useState(DIAGRAM_TEMPLATES.Flowchart);
  const [diagPreview, setDiagPreview] = useState("");

  useEffect(() => { setDiagCode(DIAGRAM_TEMPLATES[diagKind]); }, [diagKind]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const svg = await renderMermaidSVG(diagCode);
      if (!cancel) setDiagPreview(svg);
    })();
    return () => { cancel = true; };
  }, [diagCode]);

  const insertDiagram = async () => {
    const svg = await renderMermaidSVG(diagCode);
    const dataUrl = "data:image/svg+xml;utf8," + encodeURIComponent(svg);
    editor.chain().focus().setImage({ src: dataUrl, alt: `${diagKind} diagram` }).run();
    setDiagOpen(false);
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  };

  if (!editor) return null;

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-border bg-secondary/40 sticky top-0 z-10">
        <ToolGroup>
          <IB editor={editor} icon={Undo} cmd={() => editor.chain().focus().undo().run()} title="Undo" testid="rt-undo"/>
          <IB editor={editor} icon={Redo} cmd={() => editor.chain().focus().redo().run()} title="Redo" testid="rt-redo"/>
        </ToolGroup>
        <ToolGroup>
          <IB editor={editor} icon={Heading1} cmd={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} testid="rt-h1"/>
          <IB editor={editor} icon={Heading2} cmd={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} testid="rt-h2"/>
          <IB editor={editor} icon={Heading3} cmd={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} testid="rt-h3"/>
        </ToolGroup>
        <ToolGroup>
          <FontFamilyPicker editor={editor}/>
          <FontSizePicker editor={editor}/>
          <LineHeightPicker editor={editor}/>
        </ToolGroup>
        <ToolGroup>
          <IB editor={editor} icon={Bold} cmd={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} testid="rt-bold"/>
          <IB editor={editor} icon={Italic} cmd={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} testid="rt-italic"/>
          <IB editor={editor} icon={UIcon} cmd={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} testid="rt-underline"/>
          <IB editor={editor} icon={Strikethrough} cmd={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} testid="rt-strike"/>
          <IB editor={editor} icon={SupIcon} cmd={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive("superscript")} title="Superscript" testid="rt-sup"/>
          <IB editor={editor} icon={SubIcon} cmd={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive("subscript")} title="Subscript" testid="rt-sub"/>
        </ToolGroup>
        <ToolGroup>
          {HL_COLORS.map((c) => (
            <button
              key={c.value}
              title={`Highlight ${c.name}`}
              onClick={() => editor.chain().focus().toggleHighlight({ color: c.value }).run()}
              className="w-6 h-6 rounded-sm border border-border hover:scale-110 transition-transform"
              style={{ background: c.value }}
              data-testid={`rt-hl-${c.name.toLowerCase()}`}
            />
          ))}
          <IB editor={editor} icon={Eraser} cmd={() => editor.chain().focus().unsetHighlight().unsetColor().run()} title="Clear formatting" testid="rt-clear-fmt"/>
        </ToolGroup>
        <ToolGroup>
          <ColorPopover editor={editor}/>
        </ToolGroup>
        <ToolGroup>
          <IB editor={editor} icon={List} cmd={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} testid="rt-ul"/>
          <IB editor={editor} icon={ListOrdered} cmd={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} testid="rt-ol"/>
          <IB editor={editor} icon={Quote} cmd={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} testid="rt-quote"/>
          <IB editor={editor} icon={Code} cmd={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} testid="rt-code"/>
        </ToolGroup>
        <ToolGroup>
          <IB editor={editor} icon={AlignLeft} cmd={() => editor.chain().focus().setTextAlign("left").run()} testid="rt-al"/>
          <IB editor={editor} icon={AlignCenter} cmd={() => editor.chain().focus().setTextAlign("center").run()} testid="rt-ac"/>
          <IB editor={editor} icon={AlignRight} cmd={() => editor.chain().focus().setTextAlign("right").run()} testid="rt-ar"/>
          <IB editor={editor} icon={AlignJustify} cmd={() => editor.chain().focus().setTextAlign("justify").run()} title="Justify" testid="rt-aj"/>
        </ToolGroup>
        <ToolGroup>
          <IB editor={editor} icon={IndentDecrease} cmd={() => editor.chain().focus().outdentParagraph().run()} title="Outdent" testid="rt-outdent"/>
          <IB editor={editor} icon={IndentIncrease} cmd={() => editor.chain().focus().indentParagraph().run()} title="Indent" testid="rt-indent"/>
        </ToolGroup>
        <ToolGroup>
          <IB editor={editor} icon={TableIcon} cmd={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table" testid="rt-table"/>
          <IB editor={editor} icon={Link2} cmd={insertLink} title="Link" testid="rt-link"/>
          <IB editor={editor} icon={Minus} cmd={() => editor.chain().focus().insertPageBreak().run()} title="Insert page break" testid="rt-pagebreak"/>
        </ToolGroup>
        <ToolGroup>
          <button
            onClick={() => setDiagOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-secondary text-sm"
            title="Insert diagram"
            data-testid="rt-diagram"
          >
            <Workflow className="w-4 h-4"/> Diagram
          </button>
        </ToolGroup>
      </div>

      <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }} className="rounded-md border border-border bg-popover shadow-md p-1 flex gap-1">
        <IB editor={editor} icon={Bold} cmd={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}/>
        <IB editor={editor} icon={Italic} cmd={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}/>
        <IB editor={editor} icon={Highlighter} cmd={() => editor.chain().focus().toggleHighlight({ color: "#FEF3C7" }).run()} active={editor.isActive("highlight")}/>
      </BubbleMenu>

      <div className="p-5">
        <EditorContent editor={editor} data-testid="rt-editor-content"/>
      </div>

      {/* Diagram dialog */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Insert diagram</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={diagKind} onValueChange={(v)=>setDiagKind(v)}>
                  <SelectTrigger data-testid="diagram-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {Object.keys(DIAGRAM_TEMPLATES).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Diagram code (Mermaid)</Label>
                <Textarea value={diagCode} onChange={(e)=>setDiagCode(e.target.value)} className="font-mono text-xs min-h-[280px]" data-testid="diagram-code"/>
              </div>
              <div className="flex gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Workflow className="w-3 h-3"/>Flowchart</span>
                <span className="inline-flex items-center gap-1"><GitBranch className="w-3 h-3"/>Workflow</span>
                <span className="inline-flex items-center gap-1"><Network className="w-3 h-3"/>Mindmap</span>
              </div>
            </div>
            <div>
              <Label>Preview</Label>
              <div className="border border-border rounded-md p-3 bg-secondary/40 min-h-[320px] max-h-[420px] overflow-auto flex items-center justify-center" dangerouslySetInnerHTML={{ __html: diagPreview }} data-testid="diagram-preview"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDiagOpen(false)}>Cancel</Button>
            <Button onClick={insertDiagram} data-testid="diagram-insert"><ImageIcon className="w-4 h-4 mr-1"/>Insert into draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToolGroup({ children }) {
  return <div className="flex items-center gap-0.5 pr-1 mr-1 border-r border-border last:border-r-0 last:mr-0 last:pr-0">{children}</div>;
}

function IB({ icon: Icon, cmd, active, title, testid }) {
  return (
    <button
      onClick={cmd}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 grid place-items-center rounded-md hover:bg-secondary transition-colors ${active ? "bg-primary/15 text-primary" : ""}`}
    >
      <Icon className="w-4 h-4"/>
    </button>
  );
}

function ColorPopover({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((s)=>!s)}
        className="w-8 h-8 grid place-items-center rounded-md hover:bg-secondary"
        title="Text color"
        data-testid="rt-color"
      >
        <Palette className="w-4 h-4"/>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 p-2 rounded-md border border-border bg-popover shadow-md z-30 flex gap-1.5">
          {TEXT_COLORS.map((c) => (
            <button key={c} onClick={() => { editor.chain().focus().setColor(c).run(); setOpen(false); }}
              className="w-5 h-5 rounded-full border border-border" style={{ background: c }}/>
          ))}
        </div>
      )}
    </div>
  );
}

export function RichContentPreview({ html }) {
  return <div className="prose-editor" dangerouslySetInnerHTML={{ __html: html || "" }} />;
}

function FontFamilyPicker({ editor }) {
  const current = editor.getAttributes("textStyle").fontFamily || "";
  const savedRef = useRef(null);
  return (
    <Select
      value={current}
      onOpenChange={(o) => { if (o) savedRef.current = { from: editor.state.selection.from, to: editor.state.selection.to }; }}
      onValueChange={(v)=>{
        const sel = savedRef.current;
        const chain = editor.chain();
        if (sel && sel.from !== sel.to) chain.setTextSelection(sel);
        chain.focus();
        if (!v || !v.trim()) chain.unsetFontFamily(); else chain.setFontFamily(v);
        chain.run();
      }}
    >
      <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="rt-font-family"><SelectValue placeholder="Font"/></SelectTrigger>
      <SelectContent>{FONT_FAMILIES.map(f => <SelectItem key={f.label} value={f.value || " "} style={{fontFamily: f.value || undefined}}>{f.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function FontSizePicker({ editor }) {
  const current = editor.getAttributes("textStyle").fontSize || "";
  const savedRef = useRef(null);
  return (
    <Select
      value={current}
      onOpenChange={(o) => { if (o) savedRef.current = { from: editor.state.selection.from, to: editor.state.selection.to }; }}
      onValueChange={(v)=>{
        const sel = savedRef.current;
        const chain = editor.chain();
        if (sel && sel.from !== sel.to) chain.setTextSelection(sel);
        chain.focus();
        if (!v || !v.trim()) chain.unsetFontSize(); else chain.setFontSize(v);
        chain.run();
      }}
    >
      <SelectTrigger className="h-8 text-xs w-[70px]" data-testid="rt-font-size"><SelectValue placeholder="Size"/></SelectTrigger>
      <SelectContent>{FONT_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function LineHeightPicker({ editor }) {
  const current = editor.getAttributes("paragraph").lineHeight || editor.getAttributes("heading").lineHeight || "";
  const savedRef = useRef(null);
  return (
    <Select
      value={current}
      onOpenChange={(o) => { if (o) savedRef.current = { from: editor.state.selection.from, to: editor.state.selection.to }; }}
      onValueChange={(v)=>{
        const sel = savedRef.current;
        const chain = editor.chain();
        if (sel && sel.from !== sel.to) chain.setTextSelection(sel);
        chain.focus();
        if (!v || !v.trim()) chain.unsetLineHeight(); else chain.setLineHeight(v);
        chain.run();
      }}
    >
      <SelectTrigger className="h-8 text-xs w-[90px]" data-testid="rt-line-height"><SelectValue placeholder="Spacing"/></SelectTrigger>
      <SelectContent>{LINE_HEIGHTS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}
